import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { cn } from 'utils';

interface ScrollFadeProps {
  /** Additional classes for the outer container */
  className?: string;
  /** Fade overlay width in pixels (default: 32) */
  fadeWidth?: number;
  /** Background color for the gradient (CSS color value). Defaults to white. */
  fadeColor?: string;
}

/**
 * Horizontal scroll container with left / right fade-out overlays.
 *
 * The overlays appear only when the content can be scrolled further in that direction
 * and disappear when the scroll reaches the corresponding edge.
 */
const ScrollFade = ({
  children,
  className,
  fadeWidth = 32,
  fadeColor = '#ffffff'
}: PropsWithChildren<ScrollFadeProps>) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setShowLeft(scrollLeft > 0);
      setShowRight(scrollLeft + clientWidth < scrollWidth - 1);
    };

    update(); // initial check

    el.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const gradientLeft = `linear-gradient(to right, ${fadeColor} 0%, rgba(255,255,255,0) 100%)`;
  const gradientRight = `linear-gradient(to left, ${fadeColor} 0%, rgba(255,255,255,0) 100%)`;

  return (
    <div className="relative">
      {/* Scrollable content area */}
      <div ref={containerRef} className={cn('flex overflow-x-auto', className)}>
        {children}
      </div>

      {/* Left fade overlay */}
      {showLeft && (
        <div
          className="pointer-events-none absolute inset-y-0 left-0"
          style={{ width: fadeWidth, backgroundImage: gradientLeft }}
        />
      )}
      {/* Right fade overlay */}
      {showRight && (
        <div
          className="pointer-events-none absolute inset-y-0 right-0"
          style={{ width: fadeWidth, backgroundImage: gradientRight }}
        />
      )}
    </div>
  );
};

export default ScrollFade;
